import type { ReactNode } from 'react';
import classNames from 'classnames';

import { ChartKeySwatch, type ChartKeySwatchProps } from './ChartKeySwatch';

import styles from './ChartKey.module.scss';

export interface ChartKeyEntry {
    id: string;
    label: ReactNode;
    /** One mark, or several side by side (two reference lines named by one label). */
    swatch?: ChartKeySwatchProps | ChartKeySwatchProps[];
}

export interface ChartKeyProps {
    entries: ChartKeyEntry[];
    /** Keeps each label on one line; off where the labels may wrap under the chart. */
    nowrapLabels?: boolean;
    'aria-label'?: string;
    /** Placement, and the gaps through --chart-key-gap-x and --chart-key-gap-y. */
    className?: string;
}

/** A static chart key: one mark and label per series, in a wrapping row. */
export function ChartKey(props: ChartKeyProps) {
    const { entries, nowrapLabels = true, className } = props;
    return (
        <ul className={classNames(styles.key, className)} aria-label={props['aria-label']}>
            {entries.map((entry) => {
                const swatches =
                    entry.swatch === undefined
                        ? []
                        : Array.isArray(entry.swatch)
                          ? entry.swatch
                          : [entry.swatch];
                return (
                    <li key={entry.id} className={styles.entry} data-key={entry.id}>
                        {swatches.length > 1 ? (
                            <span className={styles.swatches} aria-hidden="true">
                                {swatches.map((swatch) => (
                                    <ChartKeySwatch key={swatch.color} {...swatch} />
                                ))}
                            </span>
                        ) : swatches.length === 1 ? (
                            <ChartKeySwatch {...swatches[0]} />
                        ) : null}
                        <span className={nowrapLabels ? styles.nowrap : undefined}>
                            {entry.label}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}
